import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EMPLOYMENT } from "@/components/app/InvitePanel";
import { EmployeePayrollDialog } from "@/components/app/EmployeePayroll";
import { Button } from "@/components/ui/button";

export const ROLE_INFO = {
  owner: "Full access including billing, settings and all data.",
  manager: "Manage attendance, approve timesheets & leave, rosters, announcements and invite employees.",
  employee: "Clock in/out, view own roster & timesheets, request leave, read announcements.",
};

const RoleCell = ({ m, canEdit, onSave }) => (
  canEdit ? (
    <Select value={m.role} onValueChange={(v) => onSave(m, { role: v })}>
      <SelectTrigger className="h-9 w-32 rounded-lg" data-testid={`member-role-${m.membership_id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.keys(ROLE_INFO).map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
    </Select>
  ) : <span className="capitalize">{m.role}</span>
);

export const MembersTable = () => {
  const { membership, user } = useAuth();
  const [members, setMembers] = useState([]);
  const isOwner = membership?.role === "owner";
  const payroll = membership?.payroll_access;
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/members").then((r) => setMembers(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const save = async (m, patch) => {
    try { await api.patch(`/members/${m.membership_id}`, patch); toast.success("Member updated"); load(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="members-table">
      <table className="w-full min-w-[680px] text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Name", "Role", "Employment", ...(payroll ? ["Hourly rate"] : []), "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {members.map((m) => {
            const editable = isOwner && m.user_id !== user.user_id;
            return (
              <tr key={m.membership_id} className="border-t border-slate-100" data-testid={`member-row-${m.membership_id}`}>
                <td className="px-4 py-3"><p className="font-medium">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></td>
                <td className="px-4 py-3"><RoleCell m={m} canEdit={editable} onSave={save} /></td>
                <td className="px-4 py-3">{editable ? (
                  <Select value={m.employment_type} onValueChange={(v) => save(m, { employment_type: v })}>
                    <SelectTrigger className="h-9 w-32 rounded-lg" data-testid={`member-employment-${m.membership_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(EMPLOYMENT).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>) : EMPLOYMENT[m.employment_type]}</td>
                {payroll && <td className="px-4 py-3">{m.wage_type && m.wage_type !== "hourly" ? <span className="text-xs">Fixed</span> : m.hourly_rate != null ? `$${m.hourly_rate}` : "—"}</td>}
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.status}</span>{m.app_access === "leave_only" && <span className="ml-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">Leave, news & roster</span>}</td>
                <td className="px-4 py-3">{(payroll || m.role === "employee") && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(m)} data-testid={`member-payroll-${m.membership_id}`}>{payroll ? "Pay & access" : "Access"}</Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {open && <EmployeePayrollDialog member={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
};
